<template>
  <main class="compare-hub grow">
    <section class="compare-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="compare-hero-inner">
          <h1>Compare Workspace369 with <span>the tool you use now.</span></h1>
          <p class="compare-lede">
            {{ comparisonPages.length }} side-by-side pages on features, price and fit, each with a plain
            answer on when the other tool is the better choice.
          </p>
          <div class="hub-actions">
            <a
              :href="addReferralToUrl('https://app.workspace369.com/signup')"
              data-conversion-event="start_free_click"
              data-conversion-label="Compare hub start free"
              data-conversion-location="compare-hub-hero"
            >
              Start free trial
            </a>
            <!-- Terms directly under the trial button (B1.1): on phones the buttons stack, so the line sits between them;
                 from 640px it takes its own row under both. -->
            <TrialDisclosure on-dark />
            <NuxtLink
              :to="localePath('/pricing')"
              data-conversion-event="pricing_click"
              data-conversion-label="Compare hub compare plans"
              data-conversion-location="compare-hub-hero"
            >Compare plans</NuxtLink>
          </div>
        </div>

        <form class="compare-finder" role="search" @submit.prevent>
          <label for="compare-finder-input">Find your tool</label>
          <div class="finder-field">
            <svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18"><path d="M13.5 12.1l4 4-1.4 1.4-4-4a7 7 0 111.4-1.4zM8.5 13.5a5 5 0 100-10 5 5 0 000 10z" fill="currentColor" /></svg>
            <input
              id="compare-finder-input"
              v-model="query"
              type="search"
              autocomplete="off"
              placeholder="HubSpot, HoneyBook, QuickBooks..."
              aria-controls="compare-directory"
            />
          </div>
          <div class="finder-jobs" role="group" aria-label="Filter by job">
            <button type="button" :aria-pressed="activeJob === ''" @click="activeJob = ''">All</button>
            <button
              v-for="group in groupedComparisonSections"
              :key="group.title"
              type="button"
              :aria-pressed="activeJob === group.title"
              @click="activeJob = activeJob === group.title ? '' : group.title"
            >
              {{ group.short }}
            </button>
          </div>
        </form>
      </div>
    </section>

    <section id="compare-directory" class="compare-directory" aria-label="Comparisons">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <p class="directory-count" aria-live="polite">
          <template v-if="filtering">{{ matchCount }} of {{ comparisonPages.length }} comparisons</template>
          <template v-else>{{ comparisonPages.length }} comparisons in {{ groupedComparisonSections.length }} jobs</template>
        </p>

        <section v-if="!filtering" class="directory-group directory-group--featured" aria-labelledby="compare-most">
          <h2 id="compare-most">Most compared</h2>
          <div class="directory-grid directory-grid--featured">
            <NuxtLink
              v-for="page in featuredComparisonPages"
              :key="page.slug"
              :to="localePath(page.slug)"
              class="directory-tile"
              data-conversion-event="comparison_click"
              :data-conversion-label="`Compare hub most compared ${page.competitor}`"
              data-conversion-location="compare-hub-directory"
            >
              <strong>{{ page.competitor }}</strong>
              <em>{{ page.category }}</em>
            </NuxtLink>
          </div>
        </section>

        <section
          v-for="group in visibleGroups"
          :key="group.title"
          class="directory-group"
          :aria-labelledby="group.id"
        >
          <div class="directory-group-head">
            <h2 :id="group.id">{{ group.title }}</h2>
            <p>{{ group.description }}</p>
          </div>
          <div class="directory-grid">
            <NuxtLink
              v-for="page in group.pages"
              :key="page.slug"
              :to="localePath(page.slug)"
              class="directory-tile"
              :hidden="!matches(page) || undefined"
              data-conversion-event="comparison_click"
              :data-conversion-label="`Compare hub ${page.competitor}`"
              data-conversion-location="compare-hub-directory"
            >
              <strong>{{ page.competitor }}</strong>
              <em>{{ page.category }}</em>
            </NuxtLink>
          </div>
        </section>

        <div v-if="filtering && !matchCount" class="directory-empty">
          <strong>No comparison for "{{ query }}" yet.</strong>
          <p>
            Try the
            <NuxtLink :to="localePath('/alternatives')">alternatives pages</NuxtLink>
            or tell us which tool you use on
            <NuxtLink :to="localePath('/contact-sales')">contact sales</NuxtLink>.
          </p>
        </div>
      </div>
    </section>

    <section v-if="comparisonVideoCards.length" class="compare-videos" aria-labelledby="compare-videos-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="compare-section-head">
          <h2 id="compare-videos-title">Watch the comparison videos.</h2>
          <p>Each video compares the same jobs, shown in the real Workspace369 app.</p>
        </div>
        <div class="video-row">
          <NuxtLink
            v-for="video in comparisonVideoCards"
            :key="video.youtubeId"
            :to="localePath(video.path)"
            class="video-card"
            data-conversion-event="comparison_click"
            :data-conversion-label="`Compare hub video ${video.competitor}`"
            data-conversion-location="compare-hub-videos"
          >
            <span class="video-thumb">
              <img
                :src="`https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`"
                alt=""
                width="480"
                height="360"
                loading="lazy"
                decoding="async"
              />
              <span class="video-length" translate="no">{{ video.length }}</span>
            </span>
            <strong>{{ video.competitor }} vs Workspace369</strong>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="compare-stack" aria-labelledby="compare-stack-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="compare-section-head">
          <h2 id="compare-stack-title">What one workspace replaces.</h2>
          <p>Most comparisons come down to this list: which of these jobs still need their own app.</p>
        </div>
        <div class="stack-table" role="table" aria-label="Separate tools compared with Workspace369">
          <div class="stack-row stack-row--head" role="row">
            <span role="columnheader">Job</span>
            <span role="columnheader">Usually a separate tool</span>
            <span role="columnheader">Workspace369</span>
          </div>
          <div v-for="row in workspaceStackValueRows" :key="row.stack" class="stack-row" role="row">
            <strong role="cell">{{ row.stack }}</strong>
            <span role="cell">{{ row.separateTools }}</span>
            <em role="cell">{{ row.workspace }}</em>
          </div>
        </div>
        <div class="stack-links">
          <NuxtLink
            :to="localePath('/compare/replace-your-software-stack')"
            data-conversion-event="comparison_click"
            data-conversion-label="Compare hub stack calculator"
            data-conversion-location="compare-hub-stack"
          >
            Price your current stack against one workspace <span aria-hidden="true">&rarr;</span>
          </NuxtLink>
          <NuxtLink
            :to="localePath('/why-workspace369')"
            data-conversion-event="comparison_click"
            data-conversion-label="Compare hub switching case"
            data-conversion-location="compare-hub-stack"
          >
            Read the full switching case <span aria-hidden="true">&rarr;</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <Cta03 />
    <SwitchSave location="compare-hub" />
    <PhoneStickyCta label="Compare hub" :location="$route.path" watch=".compare-hero .hub-actions" />
  </main>
</template>

<script setup>
import { localizedUrl } from "~/data/locales";
import comparisonPages from "#build/page-data/comparison-index.mjs";
import comparisonVideoCards from "#build/page-data/comparison-video-index.mjs";
import { workspaceStackValueRows } from "#build/page-data/comparison-pricing.mjs";

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = "https://workspace369.com";
const compareCanonicalUrl = computed(() => localizedUrl(siteUrl, "/compare", canonicalLocaleCode.value));
const featuredSlugs = [
  "/compare/replace-your-software-stack",
  "/compare/hubspot",
  "/compare/bonsai",
  "/compare/google-workspace-spreadsheets",
];

const featuredComparisonPages = featuredSlugs
  .map((slug) => comparisonPages.find((page) => page.slug === slug))
  .filter(Boolean);

const comparisonGroupDefinitions = [
  {
    title: "Stack replacement and operating systems",
    short: "Whole stack",
    description:
      "For buyers trying to reduce tool sprawl, spreadsheet workarounds, disconnected finance, or separate communication apps.",
    competitors: [
      "Separate tool stack",
      "Google Workspace + spreadsheets",
      "QuickBooks",
    ],
  },
  {
    title: "Phone, inbox, and customer communication",
    short: "Phone and inbox",
    description:
      "For teams comparing business calling, shared inboxes, SMS, voicemail, routing, support, and client communication workflows.",
    competitors: [
      "Google Voice",
      "Quo",
      "RingCentral",
      "Dialpad",
      "Nextiva",
      "Intercom",
      "Zendesk",
      "ZoomScheduler",
    ],
  },
  {
    title: "CRM and revenue platforms",
    short: "CRM",
    description:
      "For teams comparing sales CRMs, growth suites, pipeline systems, and configurable revenue platforms.",
    competitors: [
      "HubSpot",
      "Salesforce",
      "Pipedrive",
      "Less Annoying CRM",
      "Zoho CRM",
      "Bigin by Zoho",
      "monday.com",
      "GoHighLevel",
      "Keap",
      "Capsule CRM",
      "Insightly",
    ],
  },
  {
    title: "Clientflow and service operations",
    short: "Client flow",
    description:
      "For service businesses comparing intake, proposals, client management, scheduling, and billing workflows.",
    competitors: [
      "HoneyBook",
      "Dubsado",
      "Bonsai",
      "Bloom",
      "Zilo",
      "HoneyBook vs Dubsado",
      "TravelJoy",
      "VacationCRM",
      "Thryv",
      "Square Appointments",
    ],
  },
  {
    title: "Professional services and agency operations",
    short: "Agencies",
    description:
      "For agencies and professional-services firms comparing client onboarding, resource planning, utilization, delivery, profitability, and PSA workflows.",
    competitors: [
      "Teamwork.com",
      "Scoro",
      "Assembly (formerly Copilot)",
      "Accelo",
      "Kantata",
      "Paymo",
    ],
  },
  {
    title: "Freelance finance, time, and accounting",
    short: "Finance and time",
    description:
      "For freelancers, consultants, and agencies comparing invoicing, time, project economics, bookkeeping, tax readiness, and connected client operations.",
    competitors: [
      "FreshBooks",
      "Wave",
      "Harvest",
      "Xero",
      "QuickBooks Solopreneur",
      "Invoice Ninja",
      "Moxie",
      "17hats",
      "Plutio",
    ],
  },
  {
    title: "Projects, workspaces, and databases",
    short: "Projects",
    description:
      "For teams deciding between flexible work management, docs, databases, AI scheduling, and a client-work operating workspace.",
    competitors: [
      "Notion",
      "Motion",
      "ClickUp",
      "Asana",
      "Airtable",
      "Jira",
      "Trello",
      "Basecamp",
      "Zoho Projects",
      "Wrike",
      "Smartsheet",
    ],
  },
];

const definedSections = comparisonGroupDefinitions
  .map((group) => ({
    ...group,
    pages: group.competitors
      .map((competitor) => comparisonPages.find((page) => page.competitor === competitor))
      .filter(Boolean),
  }))
  .filter((group) => group.pages.length);
const groupedSlugs = new Set(definedSections.flatMap((group) => group.pages.map((page) => page.slug)));
const ungroupedPages = comparisonPages.filter((page) => !groupedSlugs.has(page.slug));
if (ungroupedPages.length) {
  definedSections.push({
    title: "More comparisons",
    short: "More",
    description: "Other tools buyers compare with Workspace369.",
    pages: ungroupedPages,
  });
}
const groupedComparisonSections = definedSections
  .map((group, index) => ({ ...group, id: `compare-group-${index + 1}` }));

// The finder narrows the server-rendered directory; every link stays in the HTML for crawlers.
const query = ref("");
const activeJob = ref("");
const normalize = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
const needle = computed(() => normalize(query.value));
const filtering = computed(() => Boolean(needle.value || activeJob.value));
const matches = (page) =>
  !needle.value || normalize(`${page.competitor} ${page.category}`).includes(needle.value);
const visibleGroups = computed(() =>
  groupedComparisonSections.filter(
    (group) => (!activeJob.value || group.title === activeJob.value) && group.pages.some(matches),
  ),
);
const matchCount = computed(() =>
  visibleGroups.value.reduce((total, group) => total + group.pages.filter(matches).length, 0),
);

useHead({
  title: "Compare Workspace369 | Alternatives",
  meta: [
    {
      name: "description",
      content:
        "Compare Workspace369 with HubSpot, Salesforce, Intercom, RingCentral, Dialpad, Nextiva, Zendesk, Google Voice, Zoho CRM, HoneyBook, and more.",
    },
    { name: "twitter:title", content: "Compare Workspace369" },
    {
      name: "twitter:description",
      content:
        "Workspace369 comparison pages for CRMs, project tools, clientflow tools, workspaces, AI schedulers, communication apps, and accounting systems.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Compare Workspace369" },
    {
      property: "og:description",
      content:
        "Workspace369 comparison pages for CRMs, project tools, clientflow tools, workspaces, AI schedulers, communication apps, and accounting systems.",
    },
  ],
  script: [
    {
      key: "workspace369-compare-itemlist",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            "@id": `${compareCanonicalUrl.value}#collection`,
            name: "Workspace369 Comparisons",
            url: compareCanonicalUrl.value,
            description:
              "Workspace369 comparison pages for CRMs, project tools, clientflow tools, workspaces, AI schedulers, communication apps, and accounting systems.",
            hasPart: comparisonPages.map((page) => ({
              "@type": "WebPage",
              name: page.h1,
              url: localizedUrl(siteUrl, page.slug, canonicalLocaleCode.value),
              description: page.description,
            })),
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.compare-hub {
  --hub-bg: #ffffff;
  --hub-soft: #f6f9fc;
  --hub-ink: #0f172a;
  --hub-muted: #526174;
  --hub-line: #dde6ef;
  --hub-tile: #ffffff;
  --hub-tile-hover: #f2f8fb;
  --hub-accent: #0e7490;
  --hub-accent-ink: #0b6a84;
  background: var(--hub-bg);
  color: var(--hub-ink);
  letter-spacing: 0;
}

.dark .compare-hub {
  --hub-bg: #020617;
  --hub-soft: #0b1220;
  --hub-ink: #f1f5f9;
  --hub-muted: #a5b4c6;
  --hub-line: #1f2b3d;
  --hub-tile: #0b1220;
  --hub-tile-hover: #10203a;
  --hub-accent: #22b8dd;
  --hub-accent-ink: #67e8f9;
}

/* Hero: two lines, two actions, then the finder. */
.compare-hero {
  background:
    radial-gradient(circle at 12% 10%, rgb(27 167 206 / 0.16), transparent 30rem),
    linear-gradient(180deg, #020617 0%, #0b1220 100%);
  color: #fff;
  padding: 7.25rem 0 2.5rem;
}

.compare-hero-inner {
  max-width: 46rem;
}

.compare-hero h1 {
  color: #fff;
  /* min() keeps long brand names (Workspace369) whole on 320px phones at 125% text */
  font-size: clamp(min(2.1rem, 10.8vw), 4.6vw, 3.6rem);
  font-weight: 850;
  line-height: 1.06;
  letter-spacing: 0;
  text-wrap: balance;
}

.compare-hero h1 span {
  display: block;
  color: #7dd3fc;
}

.compare-lede {
  margin-top: 1rem;
  max-width: 38rem;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.65;
}

.hub-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.6rem;
  margin-bottom: 0.6rem;
}

.hub-actions a {
  display: inline-flex;
  min-height: 2.9rem;
  min-width: 9.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  padding: 0.7rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 800;
}

/* max-width none: with its default 34rem cap the line can fit beside the buttons on wide screens instead of under them. */
.hub-actions :deep(.trial-disclosure) {
  flex: 0 0 100%;
  max-width: none;
  margin: 0;
}

@media (min-width: 640px) {
  .hub-actions :deep(.trial-disclosure) {
    order: 2;
  }
}

.hub-actions a:first-child {
  background: #0e7490;
  color: #fff;
}

.hub-actions a:first-child:hover {
  background: #0b6a84;
}

.hub-actions a:last-child {
  border: 1px solid rgb(255 255 255 / 0.22);
  color: #fff;
}

.compare-finder {
  margin-top: 2.25rem;
  border: 1px solid rgb(148 163 184 / 0.26);
  border-radius: 10px;
  background: rgb(15 23 42 / 0.72);
  padding: 1rem;
}

.compare-finder label {
  display: block;
  color: #e2e8f0;
  font-size: 0.86rem;
  font-weight: 800;
}

.finder-field {
  position: relative;
  margin-top: 0.55rem;
}

.finder-field svg {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  color: #94a3b8;
  transform: translateY(-50%);
}

.finder-field input {
  width: 100%;
  min-height: 3rem;
  border: 1px solid rgb(148 163 184 / 0.4);
  border-radius: 0.375rem;
  background: #020617;
  padding: 0.7rem 0.9rem 0.7rem 2.6rem;
  color: #fff;
  font-size: 1rem;
}

.finder-field input::placeholder {
  color: #7c8aa0;
}

.finder-field input:focus {
  border-color: #22b8dd;
  outline: 2px solid rgb(34 184 221 / 0.35);
  outline-offset: 1px;
}

.finder-jobs {
  display: flex;
  gap: 0.45rem;
  margin: 0.8rem -1rem 0;
  overflow-x: auto;
  padding: 0 1rem 0.15rem;
  scrollbar-width: none;
}

.finder-jobs::-webkit-scrollbar {
  display: none;
}

.finder-jobs button {
  flex: none;
  min-height: 2.25rem;
  border: 1px solid rgb(148 163 184 / 0.32);
  border-radius: 0.3rem;
  padding: 0.35rem 0.75rem;
  color: #cbd5e1;
  font-size: 0.84rem;
  font-weight: 700;
  white-space: nowrap;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.finder-jobs button:hover {
  border-color: #7dd3fc;
  color: #fff;
}

.finder-jobs button[aria-pressed="true"] {
  border-color: #22b8dd;
  background: rgb(34 184 221 / 0.16);
  color: #fff;
}

/* Directory: every comparison as a compact tile, grouped by job. */
.compare-directory {
  padding: 2rem 0 3.5rem;
}

.directory-count {
  color: var(--hub-muted);
  font-size: 0.86rem;
  font-weight: 700;
}

.directory-group {
  margin-top: 2rem;
  border-top: 1px solid var(--hub-line);
  padding-top: 1.25rem;
}

.directory-group h2,
.compare-section-head h2 {
  color: var(--hub-ink);
  font-size: 1.2rem;
  font-weight: 850;
  line-height: 1.25;
}

.directory-group-head p {
  margin-top: 0.3rem;
  max-width: 44rem;
  color: var(--hub-muted);
  font-size: 0.88rem;
  line-height: 1.55;
}

.directory-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.9rem;
}

.directory-tile {
  display: flex;
  min-width: 0;
  min-height: 3.6rem;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--hub-line);
  border-radius: 0.5rem;
  background: var(--hub-tile);
  padding: 0.6rem 0.75rem;
  color: var(--hub-ink);
  transition: border-color 150ms ease, background-color 150ms ease;
}

.directory-tile[hidden] {
  display: none;
}

.directory-tile:hover,
.compare-hub :is(a, button):focus-visible {
  outline: 2px solid var(--hub-accent);
  outline-offset: 2px;
}

.compare-hero :is(a, button):focus-visible {
  outline-color: #7dd3fc;
}

.directory-tile:focus-visible {
  border-color: var(--hub-accent);
  background: var(--hub-tile-hover);
}

.directory-tile strong {
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.directory-tile em,
.directory-tile span {
  /* Long compound categories (de: Solo-Geschäftsmanagement) must wrap inside the tile. */
  hyphens: auto;
  overflow-wrap: break-word;
  margin-top: 0.15rem;
  color: var(--hub-muted);
  font-size: 0.78rem;
  font-style: normal;
  line-height: 1.4;
}

.directory-group--featured {
  border-top: 0;
  padding-top: 0;
  margin-top: 1rem;
}

.directory-empty {
  margin-top: 2rem;
  border: 1px dashed var(--hub-line);
  border-radius: 0.5rem;
  padding: 1.25rem;
  color: var(--hub-ink);
}

.directory-empty p {
  margin-top: 0.35rem;
  color: var(--hub-muted);
  font-size: 0.92rem;
}

.directory-empty a {
  color: var(--hub-accent-ink);
  text-decoration: underline;
}

/* Videos and the stack table. */
.compare-videos,
.compare-stack {
  border-top: 1px solid var(--hub-line);
  padding: 3rem 0;
}

.compare-videos {
  background: var(--hub-soft);
}

.compare-section-head h2 {
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  line-height: 1.15;
}

.compare-section-head p {
  margin-top: 0.5rem;
  max-width: 40rem;
  color: var(--hub-muted);
  font-size: 0.95rem;
  line-height: 1.6;
}

.video-row {
  display: grid;
  grid-auto-columns: minmax(14rem, 72%);
  grid-auto-flow: column;
  gap: 0.9rem;
  margin: 1.4rem -1rem 0;
  overflow-x: auto;
  padding: 0 1rem 0.4rem;
  scroll-padding-inline: 1rem;
  scroll-snap-type: x mandatory;
}

.video-card {
  scroll-snap-align: start;
  color: var(--hub-ink);
}

.video-thumb {
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--hub-line);
  border-radius: 0.5rem;
  background: #0b1220;
}

.video-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 220ms ease;
}

.video-card:hover img {
  transform: scale(1.03);
}

.video-length {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  border-radius: 0.25rem;
  background: rgb(2 6 23 / 0.85);
  padding: 0.1rem 0.4rem;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
}

.video-card strong {
  display: block;
  margin-top: 0.6rem;
  font-size: 0.95rem;
  font-weight: 800;
}

.stack-table {
  margin-top: 1.4rem;
  border: 1px solid var(--hub-line);
  border-radius: 0.5rem;
  overflow: hidden;
}

.stack-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.2rem 1rem;
  border-top: 1px solid var(--hub-line);
  background: var(--hub-tile);
  padding: 0.8rem 1rem;
  font-size: 0.9rem;
}

.stack-row:first-child {
  border-top: 0;
}

.stack-row--head {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.stack-row strong {
  color: var(--hub-ink);
  font-weight: 800;
}

.stack-row span {
  grid-column: 1;
  color: var(--hub-muted);
  font-size: 0.84rem;
}

.stack-row em {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  color: var(--hub-accent-ink);
  font-size: 0.82rem;
  font-style: normal;
  font-weight: 800;
  white-space: nowrap;
}

.stack-links {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 1.25rem;
}

.stack-links a {
  /* 44px+ tap targets on phones (they were 29px), kept inline so the arrow follows a wrapped label. */
  align-self: flex-start;
  padding-block: 0.7rem;
  color: var(--hub-accent-ink);
  font-size: 0.95rem;
  font-weight: 800;
}

.stack-links a:hover {
  text-decoration: underline;
}

@media (min-width: 640px) {
  .directory-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .video-row {
    grid-auto-columns: minmax(15rem, 1fr);
    margin-inline: 0;
    padding-inline: 0;
    scroll-padding-inline: 0;
  }

  .stack-links {
    flex-direction: row;
    gap: 2rem;
  }
}

@media (min-width: 768px) {
  .compare-hero {
    padding: 8.5rem 0 3rem;
  }

  .compare-finder {
    padding: 1.15rem 1.25rem;
  }

  .finder-jobs {
    flex-wrap: wrap;
    margin-inline: 0;
    padding-inline: 0;
    overflow: visible;
  }

  .stack-row {
    grid-template-columns: minmax(12rem, 0.9fr) minmax(0, 1.3fr) 9rem;
    align-items: center;
    padding: 0.85rem 1.25rem;
  }

  .stack-row span,
  .stack-row em {
    grid-column: auto;
    grid-row: auto;
  }

  .stack-row--head {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    white-space: normal;
    display: grid;
    background: var(--hub-soft);
  }

  .stack-row--head span {
    color: var(--hub-muted);
    font-size: 0.78rem;
    font-weight: 800;
  }

  .compare-videos,
  .compare-stack {
    padding: 4.5rem 0;
  }
}

@media (min-width: 1024px) {
  .video-row {
    grid-auto-flow: row;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    overflow: visible;
  }

  .directory-group:not(.directory-group--featured) {
    display: grid;
    grid-template-columns: minmax(13rem, 0.3fr) minmax(0, 1fr);
    gap: 2rem;
  }

  .directory-group-head {
    position: sticky;
    top: 6rem;
    align-self: start;
  }

  .directory-grid {
    margin-top: 0;
  }

  .directory-grid--featured {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 0.9rem;
  }

  .directory-grid--featured .directory-tile {
    padding: 0.85rem 1rem;
  }
}

@media (max-width: 639px) {
  .directory-tile {
    padding-inline: 0.6rem;
  }

  .directory-tile strong {
    font-size: min(0.88rem, 4.4vw);
    font-weight: 700;
  }

  .directory-group-head p {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .video-thumb img,
  .directory-tile,
  .finder-jobs button {
    transition: none;
  }
}
</style>
